/**
 * The Carbon side nav (DESIGN.md "Navigation", carbon-shell `.sidenav`):
 * 256px on the shell layer with a shell-border right edge, pinned Gray 100 in
 * both themes (`data-shell`). The tenant switcher on top, then groups with
 * 12px helper headings and 32px items (40px below 1056px) at 14px/600 in
 * secondary text. Hover and selected use layer-02; selected adds primary text
 * and the 3px Soft Green bar on the leading edge. Counts trail an item in
 * helper text, or in Coral 600 behind the 12px octagon when critical. The
 * signed-in user sits at the foot above a divider, and opens a small menu
 * (feedback, sign out).
 *
 * In flow at 1056px and up. Below that it is a drawer: fixed under the
 * header, slid off-canvas and hidden (out of the tab order) until `open`.
 */
import { Menu } from '@headlessui/react'
import { Link, NavLink } from 'react-router-dom'
import clsx from 'clsx'
import { Avatar } from './Avatar'
import { IconSeverityCrit, IconUpDown } from './icons'
import type { NavItemCount, SideNavProps, SideNavUser, SideNavUserAction } from './types'

const ITEM =
  'relative flex h-8 items-center gap-3 px-4 text-body font-semibold leading-[1.29] text-text-secondary ' +
  'transition-colors duration-110 ease-carbon hover:bg-layer-2 hover:text-text-primary max-c-lg:h-10'
const SELECTED =
  'bg-layer-2 text-text-primary before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-interactive'

function Count({ count }: { count: NavItemCount }) {
  const crit = count.tone === 'crit'
  return (
    <span
      className={clsx(
        'inline-flex flex-none items-center gap-1 text-helper tabular-nums',
        crit ? 'font-semibold text-crit-ink' : 'font-normal text-text-helper',
      )}
    >
      {crit ? <IconSeverityCrit size={12} className="text-sev-crit" /> : null}
      <span aria-hidden="true">{count.n}</span>
      <span className="sr-only">, {count.aria}</span>
    </span>
  )
}

const MENU_ITEM = 'flex w-full items-center gap-3 px-4 py-2.5 text-left text-body text-text-primary'

function UserMenu({ user, actions }: { user: SideNavUser; actions: ReadonlyArray<SideNavUserAction> }) {
  const identity = (
    <>
      <Avatar initials={user.initials} tone="user" />
      <span className="min-w-0 flex-1">
        <b className="block truncate text-body font-semibold leading-[1.29]">{user.name}</b>
        {user.role ? <small className="block truncate text-helper text-text-helper">{user.role}</small> : null}
      </span>
    </>
  )
  if (actions.length === 0) {
    return <div className="flex items-center gap-3 border-t border-line-subtle px-4 py-3">{identity}</div>
  }
  return (
    <Menu as="div" className="relative border-t border-line-subtle">
      <Menu.Button
        aria-label={`${user.name}${user.role ? `, ${user.role}` : ''}. Account menu`}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-110 ease-carbon hover:bg-layer-2"
      >
        {identity}
        <IconUpDown className="text-[color:var(--icon-secondary)]" />
      </Menu.Button>
      <Menu.Items
        data-menu=""
        className="absolute bottom-full left-0 right-0 z-[70] bg-layer-2 py-1 shadow-menu focus:outline-none"
      >
        {actions.map(action => (
          <Menu.Item key={action.id}>
            {({ active }) => {
              const cls = clsx(MENU_ITEM, active && 'bg-layer-hover')
              const body = (
                <>
                  {action.icon ? (
                    <span aria-hidden="true" className="grid flex-none place-items-center text-[color:var(--icon-secondary)]">
                      {action.icon}
                    </span>
                  ) : null}
                  {action.label}
                </>
              )
              if (action.to) {
                return (
                  <Link to={action.to} className={cls}>
                    {body}
                  </Link>
                )
              }
              if (action.href) {
                return (
                  <a href={action.href} target="_blank" rel="noopener noreferrer" className={cls}>
                    {body}
                  </a>
                )
              }
              return (
                <button type="button" onClick={action.onClick} className={cls}>
                  {body}
                </button>
              )
            }}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  )
}

export function SideNav({ groups, tenantSlot, user, userActions = [], open, onClose, id = 'side-nav' }: SideNavProps) {
  return (
    <aside
      id={id}
      data-shell=""
      aria-label="Guardian navigation"
      className={clsx(
        'z-30 flex w-64 flex-none flex-col border-r border-[color:var(--shell-border)] bg-[color:var(--shell-layer)] text-text-primary',
        // z-30 in flow too: the pages' sticky strips (PillarTabs, PostureTabs, the TCM lab) are z-20 and come later
        // in the DOM, so a lower nav would let them cover the right edge of the 320px tenant menu.
        'c-lg:relative c-lg:z-30',
        // Phone drawer. Visibility rides the same 240ms transition, so the nav
        // stays visible while it slides out and leaves the tab order after.
        'max-c-lg:fixed max-c-lg:bottom-0 max-c-lg:left-0 max-c-lg:top-12',
        'max-c-lg:transition-[transform,visibility] max-c-lg:duration-240 max-c-lg:ease-carbon',
        open ? 'max-c-lg:translate-x-0' : 'max-c-lg:invisible max-c-lg:-translate-x-full',
      )}
    >
      {tenantSlot}
      <nav aria-label="Primary" className="min-h-0 flex-1 overflow-y-auto pb-4 pt-2">
        {groups.map((group, gi) => (
          <div key={group.label}>
            <div id={`${id}-group-${gi}`} className="mb-1 mt-4 px-4 text-helper text-text-helper">
              {group.label}
            </div>
            <ul aria-labelledby={`${id}-group-${gi}`} className="m-0 list-none p-0">
              {group.items.map(item => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end ?? true} onClick={onClose} className={({ isActive }) => clsx(ITEM, isActive && SELECTED)}>
                    <span aria-hidden="true" className="grid w-4 flex-none place-items-center text-[color:var(--icon-secondary)]">
                      {item.lead}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.count ? <Count count={item.count} /> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {user ? <UserMenu user={user} actions={userActions} /> : null}
    </aside>
  )
}

export default SideNav
